import React, { useState, useMemo } from 'react';
import { 
  ArrowLeft, Plus, Search, Edit3, Trash2, X, Package, Briefcase, Calendar, BookOpen,
  Image, DollarSign, Clock, MapPin, Users, Tag, Filter, MoreVertical, Eye, EyeOff,
  ChevronDown, Upload, Check, AlertCircle, User, CalendarDays, ShoppingCart, Key, Car, Ship, Hotel
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { 
  SERVICES, PRODUCTS, COURSES, RENTALS, CATEGORIES, RESOURCES,
  getEnabledModulesForOffice, isModuleEnabledForOffice 
} from '../data/database';

// Mappa moduli ai tipi di catalogo
const MODULE_TO_TAB = {
  appointments: { id: 'services', label: 'Servizi Orari', icon: Clock, color: 'emerald' },
  rentals: { id: 'rentals', label: 'Noleggi', icon: Key, color: 'sky' },
  ecommerce: { id: 'products', label: 'Prodotti', icon: Package, color: 'purple' },
  events: { id: 'events', label: 'Eventi', icon: Calendar, color: 'orange' },
  courses: { id: 'courses', label: 'Corsi', icon: BookOpen, color: 'indigo' },
  daily_bookings: { id: 'daily_bookings', label: 'Risorse', icon: CalendarDays, color: 'blue' },
  orders: { id: 'orders', label: 'Menu', icon: ShoppingCart, color: 'rose' },
};

const DAY_NAMES = {
  monday: 'Lunedì',
  tuesday: 'Martedì', 
  wednesday: 'Mercoledì',
  thursday: 'Giovedì',
  friday: 'Venerdì',
  saturday: 'Sabato',
  sunday: 'Domenica'
};

const PartnerCatalogPage = ({ onBack, onNavigate }) => {
  const { userMode, selectedOffice } = useApp();
  
  // Dynamic office data from context
  const officeId = selectedOffice?.id || 1;
  
  // NUOVA LOGICA: Legge i moduli abilitati direttamente dall'office
  const enabledModules = useMemo(() => {
    return getEnabledModulesForOffice(officeId);
  }, [officeId]);
  
  // Crea i tab dinamicamente basandosi sui moduli abilitati
  const enabledTabs = useMemo(() => {
    // Filtra solo i moduli che hanno un tab corrispondente nel catalogo
    const catalogModules = ['appointments', 'rentals', 'ecommerce', 'events', 'courses'];
    return enabledModules
      .filter(m => catalogModules.includes(m.id))
      .map(m => MODULE_TO_TAB[m.id])
      .filter(Boolean);
  }, [enabledModules]);
  
  const [activeTab, setActiveTab] = useState(enabledTabs[0]?.id || 'services');
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  
  // Local state filtered by current office
  const [partnerServices, setPartnerServices] = useState(
    SERVICES.filter(s => s.officeId === officeId)
  );
  const [partnerRentals, setPartnerRentals] = useState(
    (RENTALS || []).filter(r => r.officeId === officeId)
  );
  const [partnerProducts, setPartnerProducts] = useState(
    PRODUCTS.filter(p => p.officeId === officeId)
  );
  const [partnerEvents, setPartnerEvents] = useState([]); // Modulo eventi non implementato
  const [partnerCourses, setPartnerCourses] = useState(
    (COURSES || []).filter(c => c.officeId === officeId)
  );

  // Get operators for this business from RESOURCES
  const resourceInfo = RESOURCES?.filter(r => r.officeId === officeId && r.type === 'operator') || [];
  const operators = resourceInfo.length > 0 ? resourceInfo : [
    { id: 1, name: 'Marco', surname: 'Rossi' },
    { id: 2, name: 'Sara', surname: 'Bianchi' },
  ];

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    originalPrice: '',
    duration: '',
    category: '',
    image: '',
    stock: '',
    isActive: true,
    // Service specific
    serviceType: 'hourly', // 'hourly' or 'daily'
    operatorId: null,
    // Event specific
    date: '',
    time: '',
    endTime: '',
    location: '',
    maxParticipants: '',
    // Course specific
    level: 'Principiante',
    lessons: '',
    totalHours: '',
    schedule: [], // Weekly schedule: [{day: 'monday', time: '10:00', operatorId: 1}]
  });

  const getCurrentItems = () => {
    switch (activeTab) {
      case 'services': return partnerServices;
      case 'rentals': return partnerRentals;
      case 'products': return partnerProducts;
      case 'events': return partnerEvents;
      case 'courses': return partnerCourses;
      default: return [];
    }
  };

  const filteredItems = getCurrentItems().filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getTabConfig = () => enabledTabs.find(t => t.id === activeTab) || MODULE_TO_TAB[activeTab];

  const openAddModal = () => {
    setEditingItem(null);
    setFormData({
      name: '', description: '', price: '', originalPrice: '', duration: '',
      category: '', image: '', stock: '', isActive: true, date: '', time: '',
      endTime: '', location: '', maxParticipants: '', level: 'Principiante',
      lessons: '', totalHours: '', serviceType: 'hourly', operatorId: null, schedule: []
    });
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      description: item.description || '',
      price: item.price?.toString() || '',
      originalPrice: item.originalPrice?.toString() || '',
      duration: item.duration?.toString() || '',
      category: item.category || '',
      image: item.image || '',
      stock: item.stock?.toString() || '',
      isActive: item.isActive !== false,
      date: item.date || '',
      time: item.time || '',
      endTime: item.endTime || '',
      location: item.location || '',
      maxParticipants: item.maxParticipants?.toString() || '',
      level: item.level || 'Principiante',
      lessons: item.lessons?.toString() || '',
      totalHours: item.totalHours?.toString() || '',
      serviceType: item.serviceType || 'hourly',
      operatorId: item.operatorId || null,
      schedule: item.schedule || []
    });
    setShowModal(true);
  };

  const handleSave = () => {
    if (!formData.name.trim() || !formData.price) return;

    const itemData = {
      id: editingItem?.id || Date.now(),
      officeId: officeId,
      name: formData.name.trim(),
      description: formData.description.trim(),
      price: parseFloat(formData.price),
      originalPrice: formData.originalPrice ? parseFloat(formData.originalPrice) : null,
      category: formData.category,
      image: formData.image || 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=800',
      isActive: formData.isActive
    };

    switch (activeTab) {
      case 'services':
        itemData.serviceType = formData.serviceType; // 'hourly' or 'daily'
        itemData.operatorId = formData.operatorId;
        if (formData.serviceType === 'hourly') {
          itemData.duration = formData.duration || '30 min';
        } else {
          // For daily services (hotel rooms, rentals), duration is in days
          itemData.priceUnit = 'giorno';
        }
        if (editingItem) {
          setPartnerServices(prev => prev.map(s => s.id === editingItem.id ? itemData : s));
        } else {
          setPartnerServices(prev => [...prev, itemData]);
        }
        break;
      case 'products':
        itemData.stock = parseInt(formData.stock) || 0;
        itemData.shipping = 'both';
        if (editingItem) {
          setPartnerProducts(prev => prev.map(p => p.id === editingItem.id ? itemData : p));
        } else {
          setPartnerProducts(prev => [...prev, itemData]);
        }
        break;
      case 'events':
        itemData.date = formData.date;
        itemData.time = formData.time;
        itemData.endTime = formData.endTime;
        itemData.location = formData.location;
        itemData.operatorId = formData.operatorId;
        itemData.maxParticipants = parseInt(formData.maxParticipants) || 50;
        itemData.currentParticipants = editingItem?.currentParticipants || 0;
        if (editingItem) {
          setPartnerEvents(prev => prev.map(e => e.id === editingItem.id ? itemData : e));
        } else {
          setPartnerEvents(prev => [...prev, itemData]);
        }
        break;
      case 'courses':
        itemData.level = formData.level;
        itemData.lessons = parseInt(formData.lessons) || 1;
        itemData.totalHours = parseInt(formData.totalHours) || 1;
        itemData.maxParticipants = parseInt(formData.maxParticipants) || 20;
        itemData.currentParticipants = editingItem?.currentParticipants || 0;
        itemData.schedule = formData.schedule; // Weekly schedule with operatorId
        if (editingItem) {
          setPartnerCourses(prev => prev.map(c => c.id === editingItem.id ? itemData : c));
        } else {
          setPartnerCourses(prev => [...prev, itemData]);
        }
        break;
    }

    setShowModal(false);
  };

  const handleDelete = (id) => {
    switch (activeTab) {
      case 'services':
        setPartnerServices(prev => prev.filter(s => s.id !== id));
        break;
      case 'products':
        setPartnerProducts(prev => prev.filter(p => p.id !== id));
        break;
      case 'events':
        setPartnerEvents(prev => prev.filter(e => e.id !== id));
        break;
      case 'courses':
        setPartnerCourses(prev => prev.filter(c => c.id !== id));
        break;
    }
    setShowDeleteConfirm(null);
  };

  const toggleActive = (id) => {
    const updateFn = (items) => items.map(item => 
      item.id === id ? { ...item, isActive: !item.isActive } : item
    );
    
    switch (activeTab) {
      case 'services': setPartnerServices(updateFn); break;
      case 'products': setPartnerProducts(updateFn); break;
      case 'events': setPartnerEvents(updateFn); break;
      case 'courses': setPartnerCourses(updateFn); break;
    }
  };

  const tabConfig = getTabConfig();

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-20">
        <div className="max-w-screen-2xl mx-auto">
          <div className="flex items-center gap-4 p-4">
            <button 
              onClick={onBack} 
              className="p-2 -ml-2 hover:bg-gray-100 rounded-full"
              data-testid="catalog-back-btn"
            >
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">Gestione Catalogo</h1>
              <p className="text-sm text-gray-500">Gestisci i tuoi servizi, prodotti, eventi e corsi</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 px-4 pb-3 overflow-x-auto">
            {enabledTabs.map((type) => {
              const Icon = type.icon;
              const isActive = activeTab === type.id;
              const count = type.id === 'services' ? partnerServices.length :
                           type.id === 'products' ? partnerProducts.length :
                           type.id === 'events' ? partnerEvents.length :
                           partnerCourses.length;
              return (
                <button
                  key={type.id}
                  onClick={() => setActiveTab(type.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all ${
                    isActive 
                      ? `bg-${type.color}-500 text-white` 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  style={isActive ? { backgroundColor: type.color === 'emerald' ? '#10b981' : type.color === 'blue' ? '#3b82f6' : type.color === 'purple' ? '#8b5cf6' : '#6366f1' } : {}}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{type.label}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    isActive ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Search and Add */}
      <div className="max-w-screen-2xl mx-auto p-4">
        <div className="flex gap-3 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={`Cerca ${tabConfig?.label.toLowerCase()}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-white rounded-xl border border-gray-200 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
            />
          </div>
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 bg-emerald-500 text-white px-4 py-3 rounded-xl font-medium hover:bg-emerald-600 transition-colors"
            data-testid="add-item-btn"
          >
            <Plus className="w-5 h-5" />
            <span className="hidden sm:inline">Aggiungi</span>
          </button>
        </div>

        {/* Items Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredItems.map((item) => (
            <div 
              key={item.id} 
              className={`bg-white rounded-2xl shadow-sm overflow-hidden ${!item.isActive ? 'opacity-60' : ''}`}
              data-testid={`catalog-item-${item.id}`}
            >
              <div className="relative">
                <img 
                  src={item.image} 
                  alt={item.name}
                  className="w-full h-40 object-cover"
                />
                {!item.isActive && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <span className="bg-gray-900 text-white px-3 py-1 rounded-full text-sm font-medium">
                      Non attivo
                    </span>
                  </div>
                )}
                <div className="absolute top-3 right-3 flex gap-1">
                  <button
                    onClick={() => toggleActive(item.id)}
                    className="p-2 bg-white rounded-full shadow hover:bg-gray-50"
                    title={item.isActive ? 'Disattiva' : 'Attiva'}
                  >
                    {item.isActive ? (
                      <Eye className="w-4 h-4 text-emerald-600" />
                    ) : (
                      <EyeOff className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>
              
              <div className="p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-semibold text-gray-900 line-clamp-1">{item.name}</h3>
                  <span className="text-lg font-bold text-emerald-600">€{item.price}</span>
                </div>
                
                {item.category && (
                  <span className="inline-block text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full mb-2">
                    {item.category}
                  </span>
                )}
                
                <p className="text-sm text-gray-500 line-clamp-2 mb-3">
                  {item.description || 'Nessuna descrizione'}
                </p>
                
                <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                  {activeTab === 'services' && item.duration && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {item.duration} min
                    </span>
                  )}
                  {activeTab === 'rentals' && (
                    <>
                      <span className="flex items-center gap-1">
                        <Key className="w-4 h-4" />
                        €{item.pricePerNight || item.pricePerDay}{item.pricePerNight ? '/notte' : '/giorno'}
                      </span>
                      {(item.maxGuests || item.maxPassengers) && (
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          Max {item.maxGuests || item.maxPassengers}
                        </span>
                      )}
                    </>
                  )}
                  {activeTab === 'products' && (
                    <span className="flex items-center gap-1">
                      <Package className="w-4 h-4" />
                      {item.stock || 0} in stock
                    </span>
                  )}
                  {activeTab === 'events' && (
                    <>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {item.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {item.currentParticipants || 0}/{item.maxParticipants}
                      </span>
                    </>
                  )}
                  {activeTab === 'courses' && (
                    <>
                      <span className="flex items-center gap-1">
                        <BookOpen className="w-4 h-4" />
                        {item.lessons} lezioni
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {item.currentParticipants || 0}/{item.maxParticipants}
                      </span>
                    </>
                  )}
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => openEditModal(item)}
                    className="flex-1 flex items-center justify-center gap-1 bg-gray-100 text-gray-700 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors"
                  >
                    <Edit3 className="w-4 h-4" />
                    Modifica
                  </button>
                  <button
                    onClick={() => {
                      const typeMap = { services: 'service-detail', products: 'product-detail', events: 'event-detail', courses: 'course-detail' };
                      onNavigate(typeMap[activeTab] || 'service-detail', { id: item.id });
                    }}
                    className="flex-1 flex items-center justify-center gap-1 bg-emerald-50 text-emerald-700 py-2.5 rounded-xl text-sm font-medium hover:bg-emerald-100 transition-colors"
                    data-testid={`preview-item-${item.id}`}
                  >
                    <Eye className="w-4 h-4" />
                    Anteprima
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(item.id)}
                    className="p-2.5 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredItems.length === 0 && (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              {tabConfig && <tabConfig.icon className="w-10 h-10 text-gray-300" />}
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Nessun {tabConfig?.label.toLowerCase().slice(0, -1)} trovato
            </h3>
            <p className="text-gray-500 mb-4">
              {searchQuery ? 'Prova a modificare la ricerca' : `Aggiungi il tuo primo ${tabConfig?.label.toLowerCase().slice(0, -1)}`}
            </p>
            {!searchQuery && (
              <button
                onClick={openAddModal}
                className="inline-flex items-center gap-2 bg-emerald-500 text-white px-6 py-3 rounded-xl font-medium hover:bg-emerald-600 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Aggiungi {tabConfig?.label.slice(0, -1)}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center sm:justify-center">
          <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex items-center justify-between">
              <h3 className="text-lg font-bold">
                {editingItem ? 'Modifica' : 'Nuovo'} {tabConfig?.label.slice(0, -1)}
              </h3>
              <button 
                onClick={() => setShowModal(false)} 
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Common Fields */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nome del servizio/prodotto"
                  className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descrizione dettagliata..."
                  rows={3}
                  className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prezzo *</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="number"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      placeholder="0.00"
                      step="0.01"
                      className="w-full bg-gray-100 rounded-xl pl-9 pr-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prezzo originale</label>
                  <input
                    type="number"
                    value={formData.originalPrice}
                    onChange={(e) => setFormData({ ...formData, originalPrice: e.target.value })}
                    placeholder="Per sconti"
                    step="0.01"
                    className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="Es. Taglio, Trattamento, ecc."
                  className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL Immagine</label>
                <input
                  type="url"
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  placeholder="https://..."
                  className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              {/* Service Specific */}
              {activeTab === 'services' && (
                <>
                  {/* Service Type Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tipo di servizio</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, serviceType: 'hourly' })}
                        className={`p-3 rounded-xl border-2 text-left transition-all ${
                          formData.serviceType === 'hourly' 
                            ? 'border-emerald-500 bg-emerald-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Clock className={`w-5 h-5 mb-1 ${formData.serviceType === 'hourly' ? 'text-emerald-600' : 'text-gray-400'}`} />
                        <p className="font-medium text-gray-900">Orario</p>
                        <p className="text-xs text-gray-500">Durata in minuti/ore</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, serviceType: 'daily' })}
                        className={`p-3 rounded-xl border-2 text-left transition-all ${
                          formData.serviceType === 'daily' 
                            ? 'border-emerald-500 bg-emerald-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Calendar className={`w-5 h-5 mb-1 ${formData.serviceType === 'daily' ? 'text-emerald-600' : 'text-gray-400'}`} />
                        <p className="font-medium text-gray-900">Giornaliero</p>
                        <p className="text-xs text-gray-500">Prezzo per giorno</p>
                      </button>
                    </div>
                  </div>

                  {/* Duration (only for hourly) */}
                  {formData.serviceType === 'hourly' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Durata</label>
                      <select
                        value={formData.duration}
                        onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                        className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                      >
                        <option value="">Seleziona durata</option>
                        <option value="15 min">15 minuti</option>
                        <option value="30 min">30 minuti</option>
                        <option value="45 min">45 minuti</option>
                        <option value="1 ora">1 ora</option>
                        <option value="1h 30min">1 ora e 30 min</option>
                        <option value="2 ore">2 ore</option>
                        <option value="3 ore">3 ore</option>
                        <option value="4 ore">4 ore</option>
                      </select>
                    </div>
                  )}

                  {/* Price hint for daily */}
                  {formData.serviceType === 'daily' && (
                    <div className="bg-blue-50 rounded-xl p-3 flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-blue-700">
                        Il prezzo inserito sarà considerato <strong>per giorno</strong>. 
                        Ideale per camere, noleggi, barche, ecc.
                      </p>
                    </div>
                  )}

                  {/* Operator Assignment */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Operatore assegnato</label>
                    <select
                      value={formData.operatorId || ''}
                      onChange={(e) => setFormData({ ...formData, operatorId: e.target.value ? parseInt(e.target.value) : null })}
                      className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      <option value="">Nessun operatore specifico</option>
                      {operators.map(op => (
                        <option key={op.id} value={op.id}>
                          {op.name} {op.surname} {op.role ? `(${op.role})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {/* Product Specific */}
              {activeTab === 'products' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantità in stock</label>
                  <input
                    type="number"
                    value={formData.stock}
                    onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                    placeholder="0"
                    min="0"
                    className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              )}

              {/* Event Specific */}
              {activeTab === 'events' && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Data *</label>
                      <input
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Ora inizio</label>
                      <input
                        type="time"
                        value={formData.time}
                        onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                        className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Ora fine</label>
                      <input
                        type="time"
                        value={formData.endTime}
                        onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                        className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Max partecipanti</label>
                      <input
                        type="number"
                        value={formData.maxParticipants}
                        onChange={(e) => setFormData({ ...formData, maxParticipants: e.target.value })}
                        placeholder="50"
                        min="1"
                        className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="Indirizzo o luogo dell'evento"
                      className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                  {/* Operator Assignment for Events */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Operatore responsabile</label>
                    <select
                      value={formData.operatorId || ''}
                      onChange={(e) => setFormData({ ...formData, operatorId: e.target.value ? parseInt(e.target.value) : null })}
                      className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      <option value="">Seleziona operatore</option>
                      {operators.map(op => (
                        <option key={op.id} value={op.id}>
                          {op.name} {op.surname} {op.role ? `(${op.role})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {/* Course Specific */}
              {activeTab === 'courses' && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Livello</label>
                      <select
                        value={formData.level}
                        onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                        className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                      >
                        <option value="Principiante">Principiante</option>
                        <option value="Intermedio">Intermedio</option>
                        <option value="Avanzato">Avanzato</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">N. Lezioni</label>
                      <input
                        type="number"
                        value={formData.lessons}
                        onChange={(e) => setFormData({ ...formData, lessons: e.target.value })}
                        placeholder="10"
                        min="1"
                        className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Ore totali</label>
                      <input
                        type="number"
                        value={formData.totalHours}
                        onChange={(e) => setFormData({ ...formData, totalHours: e.target.value })}
                        placeholder="20"
                        min="1"
                        className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Max partecipanti</label>
                      <input
                        type="number"
                        value={formData.maxParticipants}
                        onChange={(e) => setFormData({ ...formData, maxParticipants: e.target.value })}
                        placeholder="20"
                        min="1"
                        className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>
                  
                  {/* Weekly Schedule */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Programmazione settimanale</label>
                    <div className="space-y-2">
                      {Object.entries(DAY_NAMES).map(([dayKey, dayLabel]) => {
                        const scheduleItem = formData.schedule?.find(s => s.day === dayKey);
                        const isEnabled = !!scheduleItem;
                        
                        return (
                          <div key={dayKey} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                            <button
                              type="button"
                              onClick={() => {
                                if (isEnabled) {
                                  setFormData({
                                    ...formData,
                                    schedule: formData.schedule.filter(s => s.day !== dayKey)
                                  });
                                } else {
                                  setFormData({
                                    ...formData,
                                    schedule: [...(formData.schedule || []), { day: dayKey, time: '10:00', operatorId: null }]
                                  });
                                }
                              }}
                              className={`w-6 h-6 rounded-lg flex items-center justify-center transition-colors ${
                                isEnabled ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-400'
                              }`}
                            >
                              {isEnabled && <Check className="w-4 h-4" />}
                            </button>
                            <span className={`w-24 text-sm ${isEnabled ? 'text-gray-900' : 'text-gray-400'}`}>
                              {dayLabel}
                            </span>
                            {isEnabled && (
                              <div className="flex items-center gap-2 flex-1">
                                <input
                                  type="time"
                                  value={scheduleItem?.time || '10:00'}
                                  onChange={(e) => {
                                    setFormData({
                                      ...formData,
                                      schedule: formData.schedule.map(s => 
                                        s.day === dayKey ? { ...s, time: e.target.value } : s
                                      )
                                    });
                                  }}
                                  className="px-2 py-1 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                                />
                                <select
                                  value={scheduleItem?.operatorId || ''}
                                  onChange={(e) => {
                                    setFormData({
                                      ...formData,
                                      schedule: formData.schedule.map(s => 
                                        s.day === dayKey ? { ...s, operatorId: e.target.value ? parseInt(e.target.value) : null } : s
                                      )
                                    });
                                  }}
                                  className="flex-1 px-2 py-1 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                                >
                                  <option value="">Nessun operatore</option>
                                  {operators.map(op => (
                                    <option key={op.id} value={op.id}>{op.name}</option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}

              {/* Active Toggle */}
              <div className="flex items-center justify-between bg-gray-50 rounded-xl p-4">
                <div>
                  <p className="font-medium text-gray-900">Visibile al pubblico</p>
                  <p className="text-sm text-gray-500">Rendi questo elemento visibile ai clienti</p>
                </div>
                <button
                  onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
                  className={`relative w-12 h-7 rounded-full transition-colors ${formData.isActive ? 'bg-emerald-500' : 'bg-gray-300'}`}
                >
                  <div className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${formData.isActive ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>
            
            <div className="sticky bottom-0 bg-white border-t border-gray-100 p-4">
              <button
                onClick={handleSave}
                disabled={!formData.name.trim() || !formData.price}
                className="w-full bg-emerald-500 text-white py-4 rounded-xl font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Check className="w-5 h-5" />
                {editingItem ? 'Salva modifiche' : 'Crea'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 text-center mb-2">Conferma eliminazione</h3>
            <p className="text-gray-500 text-center mb-6">
              Sei sicuro di voler eliminare questo elemento? L'azione non può essere annullata.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-medium hover:bg-gray-200 transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="flex-1 bg-red-500 text-white py-3 rounded-xl font-medium hover:bg-red-600 transition-colors"
              >
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerCatalogPage;
